import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import axios from "axios";
import { getURLbyEndPointV2 } from "../../../../store/api";

const BindingTimeTableModal = ({ show, handleClose, data, userId }) => {
  const initialRow = useMemo(
    () => ({ fromDate: "", toDate: "", exhibitNo: "", pages: "", file: null }),
    []
  );
  const [row1, setRow1] = useState(initialRow);
  const [row2, setRow2] = useState(initialRow);
  const [row3, setRow3] = useState(initialRow);
  const [row4, setRow4] = useState(initialRow);
  const [row5, setRow5] = useState(initialRow);
  const [row6, setRow6] = useState(initialRow);
  const [row7, setRow7] = useState(initialRow);
  const [row8, setRow8] = useState(initialRow);
  const [row9, setRow9] = useState(initialRow);
  const [row10, setRow10] = useState(initialRow);
  const [row11, setRow11] = useState(initialRow);
  const [showSectionASubmissions, setShowSectionASubmissions] = useState(false);
  const [fileView, setFileView] = useState(null);
  const [costView, setCostView] = useState(false);
  const [exchangeRate, setExchangeRate] = useState("");
  const [inrTotalAmount, setInrTotalAmount] = useState("");
  const [inrLocked, setInrLocked] = useState(false);
  const [uploads, setUploads] = useState({});
  const [savingSection, setSavingSection] = useState(null);
  const [sectionsMeta, setSectionsMeta] = useState([]);
  const [paymentLinkSent, setPaymentLinkSent] = useState(false);
  const [respondents, setRespondents] = useState([]);
  const [selectedRespondent, setSelectedRespondent] = useState("");
  const [loadingRespondents, setLoadingRespondents] = useState(false);
  const [respondentError, setRespondentError] = useState(null);
  const [conventionalHearingSchedule, setConventionalHearingSchedule] =
    useState({ date: "", time: "" });
  const [preliminaryHearingSchedule, setPreliminaryHearingSchedule] = useState({
    date: "",
    time: "",
  });
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailContent, setEmailContent] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [editingSection, setEditingSection] = useState(null);

  // New state variables for arbitrator selection
  const [arbitratorType, setArbitratorType] = useState(""); // 'sole' or 'tribunal'
  const [arbitrators, setArbitrators] = useState([]);
  const [loadingArbitrators, setLoadingArbitrators] = useState(false);
  const [arbitratorError, setArbitratorError] = useState(null);
  const [selectedArbitrators, setSelectedArbitrators] = useState({
    first: "",
    second: "",
    third: "",
  });

  const setUploadState = (sectionIndex, docKey, newState) => {
    setUploads((prev) => ({
      ...prev,
      [sectionIndex]: {
        ...(prev[sectionIndex] || {}),
        [docKey]: {
          ...(prev[sectionIndex]?.[docKey] || {}),
          ...newState,
        },
      },
    }));
  };

  const fetchRespondents = useCallback(
    async (event) => {
      if (respondents.length > 0) {
        console.log("Respondents already loaded:", respondents);
        return; // Avoid refetching if already loaded
      }
      setLoadingRespondents(true);
      setRespondentError(null);
      try {
        console.log(
          "Fetching respondents for disputeId:",
          data?._id,
          "sectionIndex: 3"
        );
        const res = await axios.get(getURLbyEndPointV2("getRespondentsList"), {
          params: { disputeId: data?._id, sectionIndex: 3 },
        });
        console.log("API response:", res.data);
        if (res?.data?.status && Array.isArray(res.data.data)) {
          setRespondents(res.data.data);
          console.log("Respondents set in state:", res.data.data);
        } else {
          setRespondentError(res.data.message || "Failed to fetch respondents");
          console.log("API error or invalid data:", res.data);
        }
      } catch (err) {
        setRespondentError("Failed to fetch respondents: " + err.message);
        console.error("Failed to fetch respondents:", err);
      } finally {
        setLoadingRespondents(false);
      }
    },
    [data?._id, respondents]
  );

  const renderCurrency = (value) =>
    value && value !== ""
      ? `$ ${Number(value).toLocaleString("en-US", {
          minimumFractionDigits: 2,
        })}`
      : "N/A";

  const [sendingPayment, setSendingPayment] = useState(false);

  const handleSendPaymentLink = async () => {
    if (!inrLocked || !inrTotalAmount) {
      alert("Please set the Total Amount (INR) in Cost of Arbitration first.");
      return;
    }
    if (!exchangeRate) {
      alert("Please enter USD → INR Rate.");
      return;
    }
    try {
      setSendingPayment(true);
      const payload = {
        amountInINR: Number(inrTotalAmount),
        selectedFeePlan: data?.paymentSchedule?.paymentPlan || "BASIC",
        exchangeRate: Number(exchangeRate),
        sectionIndex: 1,
        fromDate: row2.fromDate,
        toDate: row2.toDate,
        exhibitNo: row2.exhibitNo,
        pages: row2.pages,
      };
      const res = await axios.post(
        getURLbyEndPointV2("sendBindingPaymentReq") + data._id,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      if (res?.data?.status) {
        alert(res?.data?.message || "Payment request sent.");
        setPaymentLinkSent(true);
      } else {
        alert(res?.data?.message || "Failed to send payment request.");
      }
    } catch (err) {
      alert("Failed to send payment request.");
    } finally {
      setSendingPayment(false);
    }
  };

  const handleSendRespondentEmail = async () => {
    if (!emailContent.trim()) {
      alert("Please enter the email content.");
      return;
    }
    try {
      setEmailSending(true);
      setEmailError(null);
      const payload = {
        content: emailContent,
      };
      const res = await axios.post(
        getURLbyEndPointV2("sendRespondentEmail") + data._id,
        payload
      );
      if (res?.data?.status) {
        alert(res?.data?.message || "Email sent successfully.");
        // close form and clear
        setShowEmailForm(false);
        setEmailContent("");
        setEmailError(null);

        // --- NEW: update local timetable state so next section is enabled immediately ---
        // Mark section 1 as completed and enable section 2 (index 2)
        setSectionsMeta((prev) => {
          const next = Array.isArray(prev) ? [...prev] : [];
          // ensure section 1 exists
          next[1] = {
            ...(next[1] || {}),
            isCompleted: true,
            completedAt: new Date(),
            sectionIndex: 1,
          };
          // ensure section 2 exists and set its fromDate if empty
          next[2] = {
            ...(next[2] || {}),
            sectionIndex: 2,
            fromDate: next[2]?.fromDate || next[1]?.toDate || "",
          };
          return next;
        });
        setEnabledSection((prev) => Math.max(prev, 2));
        // --- END NEW ---
      } else {
        alert(res?.data?.message || "Failed to send email.");
      }
    } catch (err) {
      setEmailError("Failed to send email: " + err.message);
      console.error("Error sending respondent email:", err);
    } finally {
      setEmailSending(false);
    }
  };

  const UploadControl = ({ sectionIndex, docKey }) => {
    const inputId = `upload-${sectionIndex}-${docKey}`;
    const state = uploads[sectionIndex]?.[docKey]?.status;
    const file = uploads[sectionIndex]?.[docKey]?.file;
    const fileUrl = uploads[sectionIndex]?.[docKey]?.url;
    const isRowEnabled =
      isSectionEnabled(sectionIndex) && savingSection === null;
    const viewUrl = file ? URL.createObjectURL(file) : fileUrl;

    return (
      <span className="ms-2">
        <input
          id={inputId}
          type="file"
          accept="application/pdf"
          style={{ display: "none" }}
          disabled={!isRowEnabled}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            if (f.type !== "application/pdf") {
              alert("Only PDF files are accepted.");
              return;
            }
            if (f.size > 5 * 1024 * 1024) {
              alert("File size must be 5 MB or less.");
              return;
            }
            setUploadState(sectionIndex, docKey, {
              status: "selected",
              file: f,
              name: f.name,
              url: uploads[sectionIndex]?.[docKey]?.url || null,
            });
            e.target.value = "";
          }}
        />
        <span>
          {state === "selected" ? (
            <>
              {viewUrl && (
                <a
                  href={viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-decoration-underline me-2"
                >
                  View
                </a>
              )}
              <button
                type="button"
                className="text-danger text-decoration-underline bg-transparent border-0 p-0"
                onClick={() => {
                  setUploadState(sectionIndex, docKey, {
                    status: null,
                    file: null,
                    name: null,
                    url: null,
                  });
                }}
                disabled={!isRowEnabled}
              >
                Remove
              </button>
            </>
          ) : state === "uploaded" ? (
            <>
              {viewUrl && (
                <a
                  href={viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-decoration-underline me-2"
                >
                  View
                </a>
              )}
              {isRowEnabled && (
                <button
                  type="button"
                  className="text-danger text-decoration-underline bg-transparent border-0 p-0"
                  onClick={() => {
                    setUploadState(sectionIndex, docKey, {
                      status: null,
                      file: null,
                      name: null,
                      url: null,
                    });
                  }}
                >
                  Remove
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              className={`text-primary text-decoration-underline bg-transparent border-0 p-0 ${
                !isRowEnabled ? "disabled" : ""
              }`}
              onClick={() =>
                isRowEnabled && document.getElementById(inputId).click()
              }
              disabled={!isRowEnabled}
            >
              Select File
            </button>
          )}
        </span>
      </span>
    );
  };

  const [enabledSection, setEnabledSection] = useState(0);

  const formatDateInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const toRowState = useCallback(
    (row) => ({
      fromDate: row?.fromDate ? formatDateInput(row.fromDate) : "",
      toDate: row?.toDate ? formatDateInput(row.toDate) : "",
      exhibitNo: row?.exhibitNo || "",
      pages: row?.pages || "",
      file: null,
    }),
    []
  );

  const seedRowsFromArray = useCallback(
    (rows = []) => {
      setRow1(toRowState(rows[0] || {}));
      setRow2(toRowState(rows[1] || {}));
      setRow3(toRowState(rows[2] || {}));
      setRow4(toRowState(rows[3] || {}));
      setRow5(toRowState(rows[4] || {}));
      setRow6(toRowState(rows[5] || {}));
      setRow7(toRowState(rows[6] || {}));
      setRow8(toRowState(rows[7] || {}));
      setRow9(toRowState(rows[8] || {}));
      setRow10(toRowState(rows[9] || {}));
      setRow11(toRowState(rows[10] || {}));
    },
    [toRowState]
  );

  const seedUploadsFromRows = useCallback((rows = []) => {
    const nextUploads = {};
    rows.forEach((row, idx) => {
      const docs = row?.documents;
      if (!docs) return;
      let entries;
      if (docs instanceof Map) {
        entries = Array.from(docs.entries());
      } else if (Array.isArray(docs)) {
        entries = docs;
      } else {
        entries = Object.entries(docs);
      }
      entries.forEach(([docKey, url]) => {
        if (!url) return;
        if (!nextUploads[idx]) nextUploads[idx] = {};
        nextUploads[idx][docKey] = {
          status: "uploaded",
          file: null,
          name: typeof url === "string" ? url.split("/").pop() : "",
          url: typeof url === "string" ? url : "",
        };
      });
    });
    setUploads(nextUploads);
  }, []);

  const fetchTimeTableData = useCallback(async () => {
    if (!data?._id) return;
    try {
      const res = await axios.get(
        getURLbyEndPointV2("getTimeTableData") + data._id
      );
      if (res?.data?.status) {
        const { sections, enabledSection: enabledIdx } = res.data.data || {};
        const initEnabled = typeof enabledIdx === "number" ? enabledIdx : 0;
        setEnabledSection(initEnabled);
        setSectionsMeta(Array.isArray(sections) ? sections : []);
        seedRowsFromArray(sections || []);
        seedUploadsFromRows(sections || []);
      } else {
        if (typeof data?.enabledSection === "number") {
          setEnabledSection(data.enabledSection);
        }
        if (Array.isArray(data?.sections)) setSectionsMeta(data.sections);
        if (Array.isArray(data?.sections)) seedRowsFromArray(data.sections);
        if (Array.isArray(data?.sections)) seedUploadsFromRows(data.sections);
      }
    } catch (e) {
      if (typeof data?.enabledSection === "number") {
        setEnabledSection(data.enabledSection);
      }
      if (Array.isArray(data?.sections)) setSectionsMeta(data.sections);
      if (Array.isArray(data?.sections)) seedRowsFromArray(data.sections);
      if (Array.isArray(data?.sections)) seedUploadsFromRows(data.sections);
    }
  }, [data, seedRowsFromArray, seedUploadsFromRows]);

  useEffect(() => {
    if (show && data?._id) {
      fetchTimeTableData();
    }
  }, [show, data?._id, fetchTimeTableData]);

  const createHandler = (setter) => (field, value) => {
    setter((prev) => ({ ...prev, [field]: value }));
  };

  const isSectionEnabled = (idx) =>
    idx === enabledSection || idx === editingSection;

  const submissionDocs = data
    ? [
        data.correspondenceWithSupplier && {
          label: "Correspondence With Supplier",
          url: data.correspondenceWithSupplier,
        },
        data.commercialInvoice && {
          label: "Commercial Invoice",
          url: data.commercialInvoice,
        },
        data.inspectionReports && {
          label: "Inspection Reports",
          url: data.inspectionReports,
        },
        data.correspondenceWithAgent && {
          label: "Correspondence With Agent",
          url: data.correspondenceWithAgent,
        },
        data.correspondenceWithCarrier && {
          label: "Correspondence With Carrier",
          url: data.correspondenceWithCarrier,
        },
        data.photographs && { label: "Photographs", url: data.photographs },
        data.legalActionDocuments && {
          label: "Legal Action Documents",
          url: data.legalActionDocuments,
        },
        data.companyRegistrationDoc && {
          label: "Company Registration Document",
          url: data.companyRegistrationDoc,
        },
      ].filter(Boolean)
    : [];

  const handleSaveSection = async (idx, rowState) => {
    try {
      const ok = window.confirm("Do you want to save this section?");
      if (!ok) return;

      if (idx === 1) {
        if (!inrLocked || !inrTotalAmount) {
          alert(
            "Please set the Total Amount (INR) in Cost of Arbitration before saving this section."
          );
          return;
        }
        if (!exchangeRate) {
          alert("Please enter USD → INR Rate in Cost of Arbitration.");
          return;
        }
        if (!rowState.toDate || !rowState.exhibitNo || !rowState.pages) {
          alert(
            "Please fill To date, Exhibit No and No. of Pg. for this section."
          );
          return;
        }
      }

      if (idx === 7) {
        if (
          !conventionalHearingSchedule.date ||
          !conventionalHearingSchedule.time
        ) {
          alert(
            "Please select a date and time for the conventional hearing schedule."
          );
          return;
        }
      }

      if (idx === 3) {
        const respondentAlreadySaved = sectionsMeta[3]?.respondent;
        if (!selectedRespondent && !respondentAlreadySaved) {
          alert("Please select a respondent for this section.");
          return;
        }
      }

      const formData = new FormData();
      formData.append("disputeId", data?._id);
      formData.append("sectionIndex", String(idx));
      formData.append("fromDate", rowState.fromDate);
      formData.append("toDate", rowState.toDate);
      formData.append("exhibitNo", rowState.exhibitNo);
      formData.append("pages", Number(rowState.pages) || 0);

      if (idx === 1) {
        formData.append("exchangeRate", exchangeRate || "");
        formData.append("inrTotalAmount", inrTotalAmount || "");
      }

      // === UPLOAD ONLY SELECTED FILES ===
      // === UPLOAD ONLY SELECTED FILES ===
      const removedDocs = []; // Track removed documents
      if (idx >= 2) {
        const sectionUploads = uploads[idx] || {};
        const originalSectionDocs = sectionsMeta[idx]?.documents || {};

        Object.keys(sectionUploads).forEach((docKey) => {
          const item = sectionUploads[docKey];
          // Case 1: Explicit removal
          if (!item?.status && !item?.file && !item?.url) {
            removedDocs.push(docKey);
          }

          const file = item?.file;
          if (file) {
            formData.append(`files[${docKey}]`, file);

            // Case 2: Replacement (New file upload for an existing document)
            let originalUrl = null;
            if (originalSectionDocs instanceof Map) {
              originalUrl = originalSectionDocs.get(docKey);
            } else if (
              typeof originalSectionDocs === "object" &&
              originalSectionDocs !== null
            ) {
              originalUrl = originalSectionDocs[docKey];
            }

            // If there's an existing URL and we are uploading a new file,
            // mark the old one for deletion (via removedDocs).
            // Avoid adding duplicates if already pushed.
            if (originalUrl && !removedDocs.includes(docKey)) {
              removedDocs.push(docKey);
            }
          }
        });
      }
      formData.append("removedDocs", JSON.stringify(removedDocs));

      if (idx === 3) {
        let respondentData = null;

        // 1. Try to find in loaded respondents list (if user selected from dropdown)
        if (selectedRespondent && respondents.length > 0) {
          const selectedResp = respondents.find(
            (a) => a.id === selectedRespondent
          );
          if (selectedResp) {
            respondentData = {
              _id: selectedResp.id,
              firstName: selectedResp.name.split(" ")[0],
              surName: selectedResp.name.split(" ")[1] || "",
              email: selectedResp.email,
            };
          }
        }

        // 2. If not found (e.g., list not loaded) but ID matches existing saved data, use that
        if (!respondentData && sectionsMeta[3]?.respondent) {
          const saved = sectionsMeta[3].respondent;
          // If we have a selected ID, ensure it matches the saved one (or just use saved if no selection change)
          if (!selectedRespondent || selectedRespondent === saved._id) {
            respondentData = {
              _id: saved._id,
              firstName: saved.firstName,
              surName: saved.surName,
              email: saved.email,
            };
          }
        }

        if (respondentData) {
          formData.append("respondent", JSON.stringify(respondentData));
        } else if (selectedRespondent) {
          // Fallback: This shouldn't happen if logic is correct, but prevents 400 error
          // if we have an ID but no data. We might need to fetch or just warn.
          // For now, let it proceed, but backend will error if missing.
          console.warn("Respondent ID selected but data not found.");
        }
      }

      if (idx === 4) {
        formData.append(
          "hearingSchedule",
          JSON.stringify(preliminaryHearingSchedule)
        );
      }
      if (idx === 7) {
        formData.append(
          "hearingSchedule",
          JSON.stringify(conventionalHearingSchedule)
        );
      }

      // Add arbitrator selection data
      if (idx === 2) {
        const selectedArbs = [];
        if (selectedArbitrators.first) {
          const arb = arbitrators.find(
            (a) => a.id === selectedArbitrators.first
          );
          if (arb)
            selectedArbs.push({
              _id: arb.id,
              firstName: arb.name.split(" ")[0],
              surName: arb.name.split(" ")[1] || "",
              email: arb.email,
            });
        }
        if (arbitratorType === "tribunal" && selectedArbitrators.second) {
          const arb = arbitrators.find(
            (a) => a.id === selectedArbitrators.second
          );
          if (arb)
            selectedArbs.push({
              _id: arb.id,
              firstName: arb.name.split(" ")[0],
              surName: arb.name.split(" ")[1] || "",
              email: arb.email,
            });
        }
        if (arbitratorType === "tribunal" && selectedArbitrators.third) {
          const arb = arbitrators.find(
            (a) => a.id === selectedArbitrators.third
          );
          if (arb)
            selectedArbs.push({
              _id: arb.id,
              firstName: arb.name.split(" ")[0],
              surName: arb.name.split(" ")[1] || "",
              email: arb.email,
            });
        }
        formData.append("arbitratorType", arbitratorType);
        formData.append("arbitrators", JSON.stringify(selectedArbs));
      }

      setSavingSection(idx);
      const res = await axios.post(
        getURLbyEndPointV2("saveTimeTableSection") + data._id,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res?.data?.status) {
        const { section, enabledSection: nextEnabled } = res.data.data || {};
        const computedNext =
          typeof nextEnabled === "number" ? nextEnabled : idx + 1;
        if (computedNext > enabledSection) {
          setEnabledSection(computedNext);
        }

        // Reset editing state if we were editing
        if (editingSection === idx) {
          setEditingSection(null);
        }

        const updated = toRowState(section || rowState);
        // Helper to update specific row state by index
        const rowSetters = [
          setRow1,
          setRow2,
          setRow3,
          setRow4,
          setRow5,
          setRow6,
          setRow7,
          setRow8,
          setRow9,
          setRow10,
          setRow11,
        ];

        if (rowSetters[idx]) {
          rowSetters[idx](updated);
        }

        // Chain Date Logic: Update next section's From Date with this section's To Date
        // But only if we have a To date and the next section exists
        if ((section?.toDate || rowState.toDate) && rowSetters[idx + 1]) {
          const nextFromDate = section?.toDate
            ? formatDateInput(section.toDate)
            : rowState.toDate;
          rowSetters[idx + 1]((prev) => ({
            ...prev,
            fromDate: nextFromDate,
          }));
        }

        // Mark uploaded files
        if (idx >= 2) {
          const sectionUploads = uploads[idx] || {};
          Object.keys(sectionUploads).forEach((docKey) => {
            if (sectionUploads[docKey]?.status === "selected") {
              setUploadState(idx, docKey, {
                status: "uploaded",
                file: null,
                url:
                  res.data.data?.fileUrls?.[docKey] ||
                  sectionUploads[docKey]?.url,
                name: sectionUploads[docKey]?.name,
              });
            }
          });
        }

        // Refresh full data from server to ensure sync
        await fetchTimeTableData();

        // ... rest of logic ...
      } else {
        alert(res?.data?.message || "Failed to save section.");
      }
    } catch (error) {
      alert("Failed to save section.");
    } finally {
      setSavingSection(null);
    }
  };

  const paymentRequest =
    (Array.isArray(data?.sections) && data.sections[1]?.paymentRequest) ||
    (Array.isArray(data?.timeTable?.sections) &&
      data.timeTable.sections[1]?.paymentRequest) ||
    null;

  useEffect(() => {
    if (sectionsMeta[3]?.respondent?._id) {
      setSelectedRespondent(sectionsMeta[3].respondent._id);
    }
    if (sectionsMeta[4]?.meetings?.length > 0) {
      const meeting = sectionsMeta[4].meetings[0];
      const meetingDate = new Date(meeting.meetingDate);
      setPreliminaryHearingSchedule({
        date: formatDateInput(meetingDate),
        time: meeting.meetingTime || "",
      });
    }
    if (sectionsMeta[7]?.meetings?.length > 0) {
      const meeting = sectionsMeta[7].meetings[0];
      const meetingDate = new Date(meeting.meetingDate);
      setConventionalHearingSchedule({
        date: formatDateInput(meetingDate),
        time: meeting.meetingTime || "",
      });
    }
  }, [sectionsMeta]);

  const renderFeePlanDetails = () => {
    const feePlan = data?.paymentSchedule?.paymentPlan || "None";
    const paymentSchedule = data?.paymentSchedule || {};
    const arbitratorFeeDetails = paymentSchedule.arbitratorFeeDetails || {};

    return (
      <>
        <p className="mb-2">
          <strong>Fee Plan Type:</strong> {feePlan}
        </p>
        {feePlan === "FLEXIBLE" &&
          paymentSchedule.flexiblePlanInstallments?.length > 0 && (
            <>
              <h6 className="fw-bold mt-4 mb-3">Flexible Plan Installments</h6>
              <Table bordered size="sm" responsive>
                <thead>
                  <tr>
                    <th>Installment</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentSchedule.flexiblePlanInstallments.map((inst, idx) => (
                    <tr key={idx}>
                      <td>{inst.key}</td>
                      <td>{renderCurrency(inst.amount)}</td>
                      <td>{inst.isPaid ? "Paid" : "Unpaid"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        {feePlan === "FLEXIBLE" &&
          arbitratorFeeDetails.schedules?.length > 0 && (
            <>
              <h6 className="fw-bold mt-4 mb-3">Arbitrator Fee Schedules</h6>
              <Table bordered size="sm" responsive>
                <thead>
                  <tr>
                    <th>Schedule</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {arbitratorFeeDetails.schedules.map((sch, idx) => (
                    <tr key={idx}>
                      <td>{sch.key}</td>
                      <td>{renderCurrency(sch.amount)}</td>
                      <td>{sch.isPaid ? "Paid" : "Unpaid"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
      </>
    );
  };

  // Add this function to fetch arbitrators list
  const fetchArbitratorsList = useCallback(async () => {
    // Only fetch if section 2 is enabled, otherwise the backend might reject it
    if (enabledSection < 2 && !sectionsMeta[2]?.isCompleted) {
      console.log("Section 2 not enabled yet, skipping arbitrator fetch");
      return;
    }

    setLoadingArbitrators(true);
    setArbitratorError(null);
    try {
      console.log(
        "Fetching arbitrators for disputeId:",
        data?._id,
        "sectionIndex: 2"
      );
      const res = await axios.get(getURLbyEndPointV2("getArbitratorsList"), {
        params: { disputeId: data?._id, sectionIndex: 2 },
      });
      console.log("Arbitrators API response:", res.data);
      if (res?.data?.status && Array.isArray(res.data.data)) {
        setArbitrators(res.data.data);
        console.log("Arbitrators set in state:", res.data.data);
      } else {
        // If the error is about section not enabled, just ignore it if we are not in that section
        if (res.data.message?.includes("not currently enabled")) {
          console.warn("Arbitrator fetch skipped: " + res.data.message);
        } else {
          setArbitratorError(res.data.message || "Failed to fetch arbitrators");
        }
        console.log("API error or invalid data:", res.data);
      }
    } catch (err) {
      setArbitratorError("Failed to fetch arbitrators: " + err.message);
      console.error("Failed to fetch arbitrators:", err);
    } finally {
      setLoadingArbitrators(false);
    }
  }, [data?._id, enabledSection, sectionsMeta]);

  // Add this effect to load saved arbitrator data when section metadata updates
  useEffect(() => {
    if (sectionsMeta[2]?.arbitratorType) {
      setArbitratorType(sectionsMeta[2].arbitratorType.toLowerCase());
    }
    if (
      Array.isArray(sectionsMeta[2]?.arbitrators) &&
      sectionsMeta[2].arbitrators.length > 0
    ) {
      const arbs = sectionsMeta[2].arbitrators;
      setSelectedArbitrators({
        first: arbs[0]?._id || "",
        second: arbs[1]?._id || "",
        third: arbs[2]?._id || "",
      });
    }
  }, [sectionsMeta]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      aria-labelledby="binding-time-table-modal-title"
      centered
      dialogClassName="modal-90w"
      backdrop="static"
    >
      <Modal.Header closeButton={costView || fileView ? false : true}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <Modal.Title id="binding-time-table-modal-title">
            {fileView
              ? fileView.title
              : costView
              ? "Financial Details"
              : showEmailForm
              ? "Send Email to Respondent"
              : "Time Table and Record Keeping"}
            {data && (
              <div className="text-muted fs-6 mt-1">
                Application No: {data.appNo}
              </div>
            )}
          </Modal.Title>
          {fileView ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setFileView(null)}
            >
              Back
            </Button>
          ) : costView ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCostView(false)}
            >
              Back
            </Button>
          ) : showEmailForm ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowEmailForm(false);
                setEmailContent("");
                setEmailError(null);
              }}
              disabled={emailSending}
            >
              Back
            </Button>
          ) : null}
        </div>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "85vh", overflowY: "auto" }}>
        {savingSection !== null && (
          <div className="mb-2 text-primary fw-semibold">Saving...</div>
        )}
        {fileView ? (
          <div>
            <div>
              <iframe
                src={fileView.url}
                title={fileView.title}
                width="100%"
                height="700px"
                frameBorder="0"
              />
            </div>
          </div>
        ) : costView ? (
          <div>
            <div className="mb-3">
              <div className="row g-3">
                {renderCurrency(data?.quantumOfClaim) !== "N/A" && (
                  <div className="col-12 col-md-6">
                    <div>
                      <strong>Quantum of Claim:</strong>{" "}
                      {renderCurrency(data.quantumOfClaim)}
                    </div>
                  </div>
                )}
                {renderCurrency(data?.amountInDispute) !== "N/A" && (
                  <div className="col-12 col-md-6">
                    <div>
                      <strong>Amount in Dispute:</strong>{" "}
                      {renderCurrency(data.amountInDispute)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3">
              {renderFeePlanDetails()}
              <div className="table-responsive">
                <Table bordered size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Fee Type</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderCurrency(data?.paymentSchedule?.filingFee) !==
                      "N/A" && (
                      <tr>
                        <td>Filing Fee</td>
                        <td>
                          {renderCurrency(data.paymentSchedule.filingFee)}
                        </td>
                      </tr>
                    )}
                    {renderCurrency(data?.paymentSchedule?.adminFee) !==
                      "N/A" && (
                      <tr>
                        <td>
                          {data?.paymentSchedule?.paymentPlan === "FLEXIBLE"
                            ? "Admin Fee (Payable Now)"
                            : "Admin Fee"}
                        </td>
                        <td>{renderCurrency(data.paymentSchedule.adminFee)}</td>
                      </tr>
                    )}
                    {data?.paymentSchedule?.paymentPlan === "FLEXIBLE" ? (
                      <tr>
                        <td>Arbitrator Fee (Payable Now)</td>
                        <td>
                          {renderCurrency(
                            data.paymentSchedule.arbitratorFeeDetails
                              ?.payableNow
                          )}
                        </td>
                      </tr>
                    ) : (
                      renderCurrency(
                        data?.paymentSchedule?.arbitratorFeeDetails?.total
                      ) !== "N/A" && (
                        <tr>
                          <td>Arbitrator Fee</td>
                          <td>
                            {renderCurrency(
                              data.paymentSchedule.arbitratorFeeDetails.total
                            )}
                          </td>
                        </tr>
                      )
                    )}
                    {renderCurrency(data?.paymentSchedule?.totalFee) !==
                      "N/A" && (
                      <tr>
                        <td>Total Fee</td>
                        <td>{renderCurrency(data.paymentSchedule.totalFee)}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
            <div className="mt-3">
              <div className="row g-2 align-items-end">
                <div className="col-12 col-md-4">
                  <Form.Label>USD → INR Rate</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      paymentRequest
                        ? paymentRequest.exchangeRate
                        : exchangeRate
                    }
                    onChange={(e) => setExchangeRate(e.target.value)}
                    placeholder="e.g. 83.25"
                    disabled={!!paymentRequest}
                  />
                </div>
                <div className="col-12 col-md-5">
                  <Form.Label>Total Amount (INR)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      paymentRequest
                        ? paymentRequest.amountInINR
                        : inrTotalAmount
                    }
                    onChange={(e) => {
                      setInrTotalAmount(e.target.value);
                      setInrLocked(false);
                    }}
                    placeholder="Enter manually"
                    disabled={!!paymentRequest}
                  />
                </div>
                <div className="col-12 col-md-3 d-grid">
                  <Button
                    variant="primary"
                    onClick={() => {
                      if (!inrTotalAmount || Number(inrTotalAmount) <= 0) {
                        alert("Please enter a valid Total Amount (INR).");
                        return;
                      }
                      if (!exchangeRate) {
                        alert("Please enter USD → INR Rate.");
                        return;
                      }
                      setInrLocked(true);
                      setCostView(false);
                    }}
                    disabled={!!paymentRequest}
                  >
                    Set Total Amount (INR)
                  </Button>
                </div>
              </div>
              {(inrLocked || paymentRequest) && (
                <div className="text-success mt-2">
                  Total Amount (INR) set: ₹{" "}
                  {paymentRequest ? paymentRequest.amountInINR : inrTotalAmount}
                </div>
              )}
            </div>
          </div>
        ) : showEmailForm ? (
          <div>
            <Form.Group className="mb-3">
              <Form.Label>6. Relief Sought</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={emailContent}
                onChange={(e) => setEmailContent(e.target.value)}
                placeholder="Write your relief sought here..."
                disabled={emailSending}
              />
            </Form.Group>
            {emailError && <div className="text-danger mb-2">{emailError}</div>}
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={handleSendRespondentEmail}
                disabled={emailSending}
              >
                {emailSending ? "Sending..." : "Send Mail"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEmailForm(false);
                  setEmailContent("");
                  setEmailError(null);
                }}
                disabled={emailSending}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Table bordered responsive>
              <thead>
                <tr>
                  <th style={{ width: "40%" }}>Particulars</th>
                  <th style={{ width: "10%" }}>Start Date-End Date</th>
                  <th style={{ width: "15%" }}>Total No. of Days</th>
                  <th style={{ width: "15%" }}>Exhibit No. of Pages</th>
                  <th style={{ width: "20%" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>(A) ADMIN Dept</strong>
                    <p>(In Accordance with FORM A1)</p>
                    <ul style={{ marginBottom: 0 }}>
                      <li>
                        AECCI Registration (Copy of Certificate)
                        {data?.clientId?.membershipCertificate && (
                          <div className="mt-1">
                            <Button
                              variant="link"
                              className="p-0 align-baseline"
                              onClick={() =>
                                setFileView({
                                  title: "Membership Certificate",
                                  url: data.clientId.membershipCertificate,
                                })
                              }
                            >
                              Membership Certificate
                            </Button>
                          </div>
                        )}
                        {data?.clientId?.memberShipNo && (
                          <div className="text-muted">
                            Membership No: {data.clientId.memberShipNo}
                          </div>
                        )}
                      </li>
                      <li>
                        Application Submission (Case Filling, Fees etc) (F-A1)
                        {data?.arbitrationForm && (
                          <div className="mt-1">
                            <Button
                              variant="link"
                              className="p-0 align-baseline"
                              onClick={() =>
                                setFileView({
                                  title: "Arbitration Form",
                                  url: data.arbitrationForm,
                                })
                              }
                            >
                              Arbitration Form
                            </Button>
                          </div>
                        )}
                      </li>
                      <li>
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setShowSectionASubmissions((prev) => !prev)
                          }
                        >
                          Submission of Documents (refer Section I)
                          <small className="text-primary ms-2">
                            [{showSectionASubmissions ? "Hide" : "Show"}]
                          </small>
                        </span>
                        {showSectionASubmissions &&
                          submissionDocs.length > 0 && (
                            <div className="mt-2">
                              {submissionDocs.map((doc, idx) => (
                                <div key={idx}>
                                  <Button
                                    variant="link"
                                    className="p-0 align-baseline"
                                    onClick={() =>
                                      setFileView({
                                        title: doc.label,
                                        url: doc.url,
                                      })
                                    }
                                  >
                                    {doc.label}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                      </li>
                      <li>
                        Agreement of Arbitrate (E-A1)
                        {data?.agreementContract && (
                          <div className="mt-1">
                            <Button
                              variant="link"
                              className="p-0 align-baseline"
                              onClick={() =>
                                setFileView({
                                  title: "Agreement Contract",
                                  url: data.agreementContract,
                                })
                              }
                            >
                              Agreement Contract
                            </Button>
                          </div>
                        )}
                      </li>
                    </ul>
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">From</small>
                      <Form.Control
                        type="date"
                        value={row1.fromDate}
                        onChange={(e) =>
                          createHandler(setRow1)("fromDate", e.target.value)
                        }
                        className="mb-1"
                        disabled={!isSectionEnabled(0)}
                      />
                      <small className="text-muted">To</small>
                      <Form.Control
                        type="date"
                        value={row1.toDate}
                        onChange={(e) =>
                          createHandler(setRow1)("toDate", e.target.value)
                        }
                        disabled={!isSectionEnabled(0)}
                      />
                    </div>
                  </td>
                  <td>5 Days</td>
                  <td>
                    <div>
                      <small className="text-muted">Exhibit No</small>
                      <Form.Control
                        type="text"
                        value={row1.exhibitNo}
                        onChange={(e) =>
                          createHandler(setRow1)("exhibitNo", e.target.value)
                        }
                        placeholder="Enter Exhibit No"
                        className="mb-1"
                        disabled={!isSectionEnabled(0)}
                      />
                      <small className="text-muted">No. of Pg.</small>
                      <Form.Control
                        type="number"
                        value={row1.pages}
                        onChange={(e) =>
                          createHandler(setRow1)("pages", e.target.value)
                        }
                        placeholder="No. of Pages"
                        className="mb-1"
                        disabled={!isSectionEnabled(0)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2 w-100">
                      <Button
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => handleSaveSection(0, row1)}
                        disabled={
                          !isSectionEnabled(0) || savingSection !== null
                        }
                      >
                        {savingSection === 0 ? "Saving..." : "Save"}
                      </Button>
                      {!isSectionEnabled(0) && sectionsMeta[0]?.isCompleted && (
                        <div className="mt-2 text-center">
                          {editingSection === 0 ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel Edit
                            </Button>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditingSection(0)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>(B) Admin & Account Dept</strong>
                    <p>(As per FORM A3)</p>
                    <ul style={{ marginBottom: 0 }}>
                      <li>
                        <Button
                          variant="link"
                          className="p-0 align-baseline"
                          onClick={() => setCostView(true)}
                        >
                          Cost of Arbitration (E-B)
                        </Button>
                      </li>
                      {(() => {
                        const agreementId =
                          data.timeTable?.sections[1]?.agreementId;
                        const caseId = data.timeTable?.sections[1]?.caseId;
                        const printId = data.timeTable?.sections[1]?.printId;
                        const refNo =
                          data.timeTable?.sections[1]?.paymentDetails?.refNo;

                        const hasIds =
                          agreementId || caseId || printId || refNo;

                        if (hasIds) {
                          return (
                            <>
                              {agreementId && (
                                <div className="small text-muted mb-1">
                                  <span className="fw-bold">Agreement ID:</span>{" "}
                                  {agreementId}
                                </div>
                              )}
                              {caseId && (
                                <div className="small text-muted mb-1">
                                  <span className="fw-bold">Case ID:</span>{" "}
                                  {caseId}
                                </div>
                              )}
                              {printId && (
                                <div className="small text-muted mb-1">
                                  <span className="fw-bold">Print ID:</span>{" "}
                                  {printId}
                                </div>
                              )}
                              {refNo && (
                                <div className="small text-muted mb-1">
                                  <span className="fw-bold">Ref No:</span>{" "}
                                  {refNo}
                                </div>
                              )}
                            </>
                          );
                        }

                        // Only show Payment Request block if no IDs exist
                        if (paymentRequest) {
                          return (
                            <div className="border rounded bg-light p-2">
                              <div className="small fw-bold text-muted mb-2">
                                Payment Request Details:
                              </div>
                              <div className="small text-muted mb-1">
                                <span className="fw-bold">Amount (INR):</span> ₹{" "}
                                {paymentRequest.amountInINR}
                              </div>
                              <div className="small text-muted mb-1">
                                <span className="fw-bold">USD → INR Rate:</span>{" "}
                                {paymentRequest.exchangeRate}
                              </div>
                              <div className="small text-muted mb-1">
                                <span className="fw-bold">Fee Plan:</span>{" "}
                                {paymentRequest.selectedFeePlan}
                              </div>
                              <div className="small text-muted mb-1">
                                <span className="fw-bold">Sent At:</span>{" "}
                                {new Date(
                                  paymentRequest.sentAt
                                ).toLocaleString()}
                              </div>
                            </div>
                          );
                        }

                        return null;
                      })()}

                      {inrLocked && (
                        <li className="text-muted">
                          Total Amount (INR) set: ₹ {inrTotalAmount}
                        </li>
                      )}
                    </ul>
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">From</small>
                      <Form.Control
                        type="date"
                        value={row2.fromDate}
                        onChange={(e) =>
                          createHandler(setRow2)("fromDate", e.target.value)
                        }
                        className="mb-1"
                        disabled={true}
                      />
                      <small className="text-muted">To</small>
                      <Form.Control
                        type="date"
                        value={row2.toDate}
                        onChange={(e) =>
                          createHandler(setRow2)("toDate", e.target.value)
                        }
                        disabled={!isSectionEnabled(1)}
                      />
                    </div>
                  </td>
                  <td>5 Days</td>
                  <td>
                    <div>
                      <small className="text-muted">Exhibit No</small>
                      <Form.Control
                        type="text"
                        value={row2.exhibitNo}
                        onChange={(e) =>
                          createHandler(setRow2)("exhibitNo", e.target.value)
                        }
                        placeholder="Enter Exhibit No"
                        className="mb-1"
                        disabled={!isSectionEnabled(1)}
                      />
                      <small className="text-muted">No. of Pg.</small>
                      <Form.Control
                        type="number"
                        value={row2.pages}
                        onChange={(e) =>
                          createHandler(setRow2)("pages", e.target.value)
                        }
                        placeholder="No. of Pages"
                        className="mb-1"
                        disabled={!isSectionEnabled(1)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2 w-100">
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-100"
                        onClick={handleSendPaymentLink}
                        disabled={
                          !isSectionEnabled(1) ||
                          savingSection !== null ||
                          sendingPayment ||
                          !!paymentRequest ||
                          paymentLinkSent
                        }
                      >
                        {sendingPayment
                          ? "Sending..."
                          : savingSection === 1
                          ? "Saving..."
                          : "Send Payment Link"}
                      </Button>
                      {!isSectionEnabled(1) && sectionsMeta[1]?.isCompleted && (
                        <div className="mt-2 text-center">
                          {editingSection === 1 ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel Edit
                            </Button>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditingSection(1)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                      {data.timeTable?.sections[1]?.paymentDetails?.receipt && (
                        <div className="small text-muted">
                          <div className="mb-1">
                            <span className="fw-bold">Payment Completed:</span>{" "}
                            <br />
                            {new Date(
                              data.timeTable.sections[1].paymentDetails.paymentCompleteDate
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                          <a
                            href={
                              data.timeTable.sections[1].paymentDetails.receipt
                            }
                            target="_blank"
                            size="sm"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary w-100"
                          >
                            View Receipt
                          </a>

                          <Button
                            size="sm"
                            className="btn btn-info w-100 mt-3"
                            onClick={() => setShowEmailForm(true)}
                            disabled={
                              savingSection !== null || !isSectionEnabled(1)
                            }
                          >
                            Send Respondent Mail
                          </Button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>(C) LW Dept</strong>
                    <p>Intimation from Center (As per FORM A2)</p>

                    {/* Arbitrator Type Selection */}
                    {(!sectionsMeta[2]?.arbitrators ||
                      sectionsMeta[2].arbitrators.length === 0 ||
                      editingSection === 2) && (
                      <Form.Group className="mb-3">
                        <Form.Label>Select Arbitrator Type</Form.Label>
                        <Form.Select
                          value={arbitratorType}
                          onChange={(e) => {
                            const type = e.target.value;
                            setArbitratorType(type);
                            // Reset selected arbitrators when type changes
                            setSelectedArbitrators({
                              first: "",
                              second: "",
                              third: "",
                            });
                          }}
                          disabled={!isSectionEnabled(2)}
                        >
                          <option value="">Choose Arbitrator Type</option>
                          <option value="sole">Sole Arbitrator</option>
                          <option value="tribunal">
                            Tribunal (3 Arbitrators)
                          </option>
                        </Form.Select>
                      </Form.Group>
                    )}

                    {/* Arbitrator Selection Dropdowns */}
                    {arbitratorType &&
                      (!sectionsMeta[2]?.arbitrators ||
                        sectionsMeta[2].arbitrators.length === 0 ||
                        editingSection === 2) && (
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {arbitratorType === "sole"
                              ? "Select Sole Arbitrator"
                              : "Select Arbitrators for Tribunal"}
                          </Form.Label>

                          {loadingArbitrators && (
                            <div className="alert alert-info mb-2 small">
                              Loading arbitrators...
                            </div>
                          )}
                          {arbitratorError && (
                            <div className="text-danger mb-2 small">
                              {arbitratorError}
                            </div>
                          )}

                          {/* Sole Arbitrator - Single Dropdown */}
                          {arbitratorType === "sole" && (
                            <Form.Select
                              value={selectedArbitrators.first}
                              onClick={() => {
                                if (arbitrators.length === 0)
                                  fetchArbitratorsList();
                              }}
                              onChange={(e) => {
                                setSelectedArbitrators((prev) => ({
                                  ...prev,
                                  first: e.target.value,
                                }));
                              }}
                              disabled={
                                !isSectionEnabled(2) || loadingArbitrators
                              }
                            >
                              <option value="">
                                {loadingArbitrators
                                  ? "Loading..."
                                  : "Select Arbitrator"}
                              </option>
                              {arbitrators.map((arb) => (
                                <option key={arb.id} value={arb.id}>
                                  {arb.name} ({arb.email})
                                </option>
                              ))}
                            </Form.Select>
                          )}

                          {/* Tribunal - Three Dropdowns */}
                          {arbitratorType === "tribunal" && (
                            <div className="d-flex flex-column gap-2">
                              <div>
                                <small className="text-muted">
                                  First Arbitrator
                                </small>
                                <Form.Select
                                  value={selectedArbitrators.first}
                                  onClick={() => {
                                    if (arbitrators.length === 0)
                                      fetchArbitratorsList();
                                  }}
                                  onChange={(e) => {
                                    setSelectedArbitrators((prev) => ({
                                      ...prev,
                                      first: e.target.value,
                                    }));
                                  }}
                                  disabled={
                                    !isSectionEnabled(2) || loadingArbitrators
                                  }
                                >
                                  <option value="">
                                    {loadingArbitrators
                                      ? "Loading..."
                                      : "Select First Arbitrator"}
                                  </option>
                                  {arbitrators.map((arb) => (
                                    <option key={arb.id} value={arb.id}>
                                      {arb.name} ({arb.email})
                                    </option>
                                  ))}
                                </Form.Select>
                              </div>
                              <div>
                                <small className="text-muted">
                                  Second Arbitrator
                                </small>
                                <Form.Select
                                  value={selectedArbitrators.second}
                                  onClick={() => {
                                    if (arbitrators.length === 0)
                                      fetchArbitratorsList();
                                  }}
                                  onChange={(e) => {
                                    setSelectedArbitrators((prev) => ({
                                      ...prev,
                                      second: e.target.value,
                                    }));
                                  }}
                                  disabled={
                                    !isSectionEnabled(2) || loadingArbitrators
                                  }
                                >
                                  <option value="">
                                    {loadingArbitrators
                                      ? "Loading..."
                                      : "Select Second Arbitrator"}
                                  </option>
                                  {arbitrators.map((arb) => (
                                    <option key={arb.id} value={arb.id}>
                                      {arb.name} ({arb.email})
                                    </option>
                                  ))}
                                </Form.Select>
                              </div>
                              <div>
                                <small className="text-muted">
                                  Third Arbitrator
                                </small>
                                <Form.Select
                                  value={selectedArbitrators.third}
                                  onClick={() => {
                                    if (arbitrators.length === 0)
                                      fetchArbitratorsList();
                                  }}
                                  onChange={(e) => {
                                    setSelectedArbitrators((prev) => ({
                                      ...prev,
                                      third: e.target.value,
                                    }));
                                  }}
                                  disabled={
                                    !isSectionEnabled(2) || loadingArbitrators
                                  }
                                >
                                  <option value="">
                                    {loadingArbitrators
                                      ? "Loading..."
                                      : "Select Third Arbitrator"}
                                  </option>
                                  {arbitrators.map((arb) => (
                                    <option key={arb.id} value={arb.id}>
                                      {arb.name} ({arb.email})
                                    </option>
                                  ))}
                                </Form.Select>
                              </div>
                            </div>
                          )}
                        </Form.Group>
                      )}

                    {/* Display Selected Arbitrators - Hide when editing */}
                    {Array.isArray(sectionsMeta[2]?.arbitrators) &&
                      sectionsMeta[2].arbitrators.length > 0 &&
                      editingSection !== 2 && (
                        <div className="mb-2 p-2 bg-light border rounded">
                          <div className="small fw-bold mb-1">
                            Selected Arbitrators
                          </div>
                          <ul className="mb-0" style={{ marginBottom: 0 }}>
                            {sectionsMeta[2].arbitrators.map((arb, i) => (
                              <li key={arb._id || i}>
                                {`${arb.firstName || ""} ${
                                  arb.surName || ""
                                }`.trim()}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                    {/* Upload Controls - Show based on selection */}
                    <ul style={{ marginBottom: 0 }}>
                      {arbitratorType === "tribunal" ? (
                        <>
                          {/* Tribunal: Show uploads for each selected arbitrator */}
                          {selectedArbitrators.first && (
                            <li>
                              Appointment Letter - Arbitrator 1 (E-C1-Arb1)
                              <UploadControl
                                sectionIndex={2}
                                docKey="E-C1-Arb1"
                              />
                            </li>
                          )}
                          {selectedArbitrators.second && (
                            <li>
                              Appointment Letter - Arbitrator 2 (E-C1-Arb2)
                              <UploadControl
                                sectionIndex={2}
                                docKey="E-C1-Arb2"
                              />
                            </li>
                          )}
                          {selectedArbitrators.third && (
                            <li>
                              Appointment Letter - Arbitrator 3 (E-C1-Arb3)
                              <UploadControl
                                sectionIndex={2}
                                docKey="E-C1-Arb3"
                              />
                            </li>
                          )}
                          {/* If no arbitrators selected yet, show a placeholder or nothing */}
                          {!selectedArbitrators.first &&
                            !selectedArbitrators.second &&
                            !selectedArbitrators.third && (
                              <li className="text-muted small">
                                Please select arbitrators to show upload
                                options.
                              </li>
                            )}
                        </>
                      ) : (
                        <li>
                          Appointment Letter - Arbitrator 1 (E-C1-Arb1)
                          <UploadControl sectionIndex={2} docKey="E-C1-Arb1" />
                        </li>
                      )}

                      <li>
                        To Claimant of Arbitrator Appointment (E-C2)
                        <UploadControl sectionIndex={2} docKey="E-C2" />
                      </li>
                      <li>
                        To Respondent of Arbitrator Appointment (E-C3)
                        <UploadControl sectionIndex={2} docKey="E-C3" />
                      </li>
                    </ul>
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">From</small>
                      <Form.Control
                        type="date"
                        value={row3.fromDate}
                        onChange={(e) =>
                          createHandler(setRow3)("fromDate", e.target.value)
                        }
                        className="mb-1"
                        disabled={true}
                      />
                      <small className="text-muted">To</small>
                      <Form.Control
                        type="date"
                        value={row3.toDate}
                        onChange={(e) =>
                          createHandler(setRow3)("toDate", e.target.value)
                        }
                        disabled={!isSectionEnabled(2)}
                      />
                    </div>
                  </td>
                  <td>8 Days</td>
                  <td>
                    <div>
                      <small className="text-muted">Exhibit No</small>
                      <Form.Control
                        type="text"
                        value={row3.exhibitNo}
                        onChange={(e) =>
                          createHandler(setRow3)("exhibitNo", e.target.value)
                        }
                        placeholder="Enter Exhibit No"
                        className="mb-1"
                        disabled={!isSectionEnabled(2)}
                      />
                      <small className="text-muted">No. of Pg.</small>
                      <Form.Control
                        type="number"
                        value={row3.pages}
                        onChange={(e) =>
                          createHandler(setRow3)("pages", e.target.value)
                        }
                        placeholder="No. of Pages"
                        className="mb-1"
                        disabled={!isSectionEnabled(2)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2 w-100">
                      <Button
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => handleSaveSection(2, row3)}
                        disabled={
                          !isSectionEnabled(2) ||
                          savingSection !== null ||
                          !arbitratorType ||
                          !selectedArbitrators.first
                        }
                      >
                        {savingSection === 2 ? "Saving..." : "Save"}
                      </Button>
                      {!isSectionEnabled(2) && sectionsMeta[2]?.isCompleted && (
                        <div className="mt-2 text-center">
                          {editingSection === 2 ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel Edit
                            </Button>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditingSection(2)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>(D) LW Dept</strong>
                    <p>Filing of Response</p>
                    {sectionsMeta[3]?.respondent ? (
                      <Form.Group className="mb-2">
                        <Form.Label>Selected Respondent</Form.Label>
                        <Form.Control
                          type="text"
                          value={`${sectionsMeta[3].respondent.firstName} ${
                            sectionsMeta[3].respondent.surName || ""
                          }`}
                          disabled
                        />
                      </Form.Group>
                    ) : (
                      <Form.Group className="mb-2">
                        <Form.Label>Select Respondent</Form.Label>
                        <Form.Select
                          value={selectedRespondent}
                          onClick={(e) => {
                            e.preventDefault(); // Prevent default to keep dropdown open
                            console.log(
                              "Dropdown clicked, fetching respondents"
                            );
                            fetchRespondents(e); // Pass event to fetchRespondents
                          }}
                          onChange={(e) => {
                            console.log(
                              "Selected respondent ID:",
                              e.target.value
                            );
                            setSelectedRespondent(e.target.value);
                          }}
                          disabled={!isSectionEnabled(3) || loadingRespondents}
                        >
                          <option value="">
                            {loadingRespondents
                              ? "Loading..."
                              : "Select Respondent"}
                          </option>
                          {respondents.length > 0 ? (
                            respondents.map((resp) => (
                              <option key={resp.id} value={resp.id}>
                                {resp.name} ({resp.email})
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              No respondents available
                            </option>
                          )}
                        </Form.Select>
                        {respondentError && (
                          <div className="text-danger mt-1">
                            {respondentError}
                          </div>
                        )}
                      </Form.Group>
                    )}
                    <ul style={{ marginBottom: 0 }}>
                      <li>
                        From Claimant (E-D1)
                        <UploadControl sectionIndex={3} docKey="E-D1" />
                      </li>
                      <li>
                        From Respondent (E-D2)
                        <UploadControl sectionIndex={3} docKey="E-D2" />
                      </li>
                    </ul>
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">From</small>
                      <Form.Control
                        type="date"
                        value={row4.fromDate}
                        onChange={(e) =>
                          createHandler(setRow4)("fromDate", e.target.value)
                        }
                        className="mb-1"
                        disabled={true}
                      />
                      <small className="text-muted">To</small>
                      <Form.Control
                        type="date"
                        value={row4.toDate}
                        onChange={(e) =>
                          createHandler(setRow4)("toDate", e.target.value)
                        }
                        disabled={!isSectionEnabled(3)}
                      />
                    </div>
                  </td>
                  <td>15 Days</td>
                  <td>
                    <div>
                      <small className="text-muted">Exhibit No</small>
                      <Form.Control
                        type="text"
                        value={row4.exhibitNo}
                        onChange={(e) =>
                          createHandler(setRow4)("exhibitNo", e.target.value)
                        }
                        placeholder="Enter Exhibit No"
                        className="mb-1"
                        disabled={!isSectionEnabled(3)}
                      />
                      <small className="text-muted">No. of Pg.</small>
                      <Form.Control
                        type="number"
                        value={row4.pages}
                        onChange={(e) =>
                          createHandler(setRow4)("pages", e.target.value)
                        }
                        placeholder="No. of Pages"
                        className="mb-1"
                        disabled={!isSectionEnabled(3)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2 w-100">
                      <Button
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => handleSaveSection(3, row4)}
                        disabled={
                          !isSectionEnabled(3) || savingSection !== null
                        }
                      >
                        {savingSection === 3 ? "Saving..." : "Save"}
                      </Button>
                      {!isSectionEnabled(3) && sectionsMeta[3]?.isCompleted && (
                        <div className="mt-2 text-center">
                          {editingSection === 3 ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel Edit
                            </Button>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditingSection(3)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>(E) LW Dept & Arbitrator</strong>
                    <p>Preliminary Hearing</p>
                    <Form.Group className="mb-2">
                      <Form.Label>Schedule Hearing</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="date"
                          value={preliminaryHearingSchedule.date}
                          onChange={(e) =>
                            setPreliminaryHearingSchedule((prev) => ({
                              ...prev,
                              date: e.target.value,
                            }))
                          }
                          disabled={!isSectionEnabled(4)}
                        />
                        <Form.Control
                          type="time"
                          value={preliminaryHearingSchedule.time}
                          onChange={(e) =>
                            setPreliminaryHearingSchedule((prev) => ({
                              ...prev,
                              time: e.target.value,
                            }))
                          }
                          disabled={!isSectionEnabled(4)}
                        />
                      </div>
                    </Form.Group>
                    <ul style={{ marginBottom: 0 }}>
                      <li>
                        Enquiry by Arbitrator in Writing
                        <UploadControl sectionIndex={4} docKey="Enquiry" />
                      </li>
                      <li>
                        Counter Claim (by Respondent/Claimant)
                        <UploadControl sectionIndex={4} docKey="CounterClaim" />
                      </li>
                      <li>
                        Production of Additional Information & Documents
                        <UploadControl sectionIndex={4} docKey="Production" />
                      </li>
                    </ul>
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">From</small>
                      <Form.Control
                        type="date"
                        value={row5.fromDate}
                        onChange={(e) =>
                          createHandler(setRow5)("fromDate", e.target.value)
                        }
                        className="mb-1"
                        disabled={true}
                      />
                      <small className="text-muted">To</small>
                      <Form.Control
                        type="date"
                        value={row5.toDate}
                        onChange={(e) =>
                          createHandler(setRow5)("toDate", e.target.value)
                        }
                        disabled={!isSectionEnabled(4)}
                      />
                    </div>
                  </td>
                  <td>10 Days</td>
                  <td>
                    <div>
                      <small className="text-muted">Exhibit No</small>
                      <Form.Control
                        type="text"
                        value={row5.exhibitNo}
                        onChange={(e) =>
                          createHandler(setRow5)("exhibitNo", e.target.value)
                        }
                        placeholder="Enter Exhibit No"
                        className="mb-1"
                        disabled={!isSectionEnabled(4)}
                      />
                      <small className="text-muted">No. of Pg.</small>
                      <Form.Control
                        type="number"
                        value={row5.pages}
                        onChange={(e) =>
                          createHandler(setRow5)("pages", e.target.value)
                        }
                        placeholder="No. of Pages"
                        className="mb-1"
                        disabled={!isSectionEnabled(4)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2 w-100">
                      <Button
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => handleSaveSection(4, row5)}
                        disabled={
                          !isSectionEnabled(4) || savingSection !== null
                        }
                      >
                        {savingSection === 4 ? "Saving..." : "Save"}
                      </Button>
                      {!isSectionEnabled(4) && sectionsMeta[4]?.isCompleted && (
                        <div className="mt-2 text-center">
                          {editingSection === 4 ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel Edit
                            </Button>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditingSection(4)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}

                      {sectionsMeta[4]?.meetings?.length > 0 && (
                        <MeetingJoinButton
                          meetings={sectionsMeta[4].meetings}
                        />
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>(F) LW Dept</strong>
                    <p>
                      File of Additional Information & Documents (along with
                      written submission)
                    </p>
                    <ul style={{ marginBottom: 0 }}>
                      <li>
                        By Claimant
                        <UploadControl sectionIndex={5} docKey="ByClaimant" />
                      </li>
                      <li>
                        By Respondent
                        <UploadControl sectionIndex={5} docKey="ByRespondent" />
                      </li>
                    </ul>
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">From</small>
                      <Form.Control
                        type="date"
                        value={row6.fromDate}
                        onChange={(e) =>
                          createHandler(setRow6)("fromDate", e.target.value)
                        }
                        className="mb-1"
                        disabled={true}
                      />
                      <small className="text-muted">To</small>
                      <Form.Control
                        type="date"
                        value={row6.toDate}
                        onChange={(e) =>
                          createHandler(setRow6)("toDate", e.target.value)
                        }
                        disabled={!isSectionEnabled(5)}
                      />
                    </div>
                  </td>
                  <td>10 Days</td>
                  <td>
                    <div>
                      <small className="text-muted">Exhibit No</small>
                      <Form.Control
                        type="text"
                        value={row6.exhibitNo}
                        onChange={(e) =>
                          createHandler(setRow6)("exhibitNo", e.target.value)
                        }
                        placeholder="Enter Exhibit No"
                        className="mb-1"
                        disabled={!isSectionEnabled(5)}
                      />
                      <small className="text-muted">No. of Pg.</small>
                      <Form.Control
                        type="number"
                        value={row6.pages}
                        onChange={(e) =>
                          createHandler(setRow6)("pages", e.target.value)
                        }
                        placeholder="No. of Pages"
                        className="mb-1"
                        disabled={!isSectionEnabled(5)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2 w-100">
                      <Button
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => handleSaveSection(5, row6)}
                        disabled={
                          !isSectionEnabled(5) || savingSection !== null
                        }
                      >
                        {savingSection === 5 ? "Saving..." : "Save"}
                      </Button>
                      {!isSectionEnabled(5) && sectionsMeta[5]?.isCompleted && (
                        <div className="mt-2 text-center">
                          {editingSection === 5 ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel Edit
                            </Button>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditingSection(5)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>(G) LW Dept</strong>
                    <p>Hearing (Notices Serve by Centre)</p>
                    <ul style={{ marginBottom: 0 }}>
                      {sectionsMeta[2]?.arbitrators?.length > 1 ? (
                        sectionsMeta[2].arbitrators.map((arb, i) => (
                          <li key={`E-G-Arb${i + 1}`}>
                            To Arbitrator {i + 1} ({arb.firstName} {arb.surName}
                            )
                            <UploadControl
                              sectionIndex={6}
                              docKey={`E-G-Arb${i + 1}`}
                            />
                          </li>
                        ))
                      ) : (
                        <li>
                          To Sole Arbitrator/Tribunal
                          <UploadControl sectionIndex={6} docKey="ToTribunal" />
                        </li>
                      )}
                      <li>
                        To Claimant (E-G1)
                        <UploadControl sectionIndex={6} docKey="E-G1" />
                      </li>
                      <li>
                        To Respondent (E-G2)
                        <UploadControl sectionIndex={6} docKey="E-G2" />
                      </li>
                    </ul>
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">From</small>
                      <Form.Control
                        type="date"
                        value={row7.fromDate}
                        onChange={(e) =>
                          createHandler(setRow7)("fromDate", e.target.value)
                        }
                        className="mb-1"
                        disabled={true}
                      />
                      <small className="text-muted">To</small>
                      <Form.Control
                        type="date"
                        value={row7.toDate}
                        onChange={(e) =>
                          createHandler(setRow7)("toDate", e.target.value)
                        }
                        disabled={!isSectionEnabled(6)}
                      />
                    </div>
                  </td>
                  <td>7 Days</td>
                  <td>
                    <div>
                      <small className="text-muted">Exhibit No</small>
                      <Form.Control
                        type="text"
                        value={row7.exhibitNo}
                        onChange={(e) =>
                          createHandler(setRow7)("exhibitNo", e.target.value)
                        }
                        placeholder="Enter Exhibit No"
                        className="mb-1"
                        disabled={!isSectionEnabled(6)}
                      />
                      <small className="text-muted">No. of Pg.</small>
                      <Form.Control
                        type="number"
                        value={row7.pages}
                        onChange={(e) =>
                          createHandler(setRow7)("pages", e.target.value)
                        }
                        placeholder="No. of Pages"
                        className="mb-1"
                        disabled={!isSectionEnabled(6)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2 w-100">
                      <Button
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => handleSaveSection(6, row7)}
                        disabled={
                          !isSectionEnabled(6) || savingSection !== null
                        }
                      >
                        {savingSection === 6 ? "Saving..." : "Save"}
                      </Button>
                      {!isSectionEnabled(6) && sectionsMeta[6]?.isCompleted && (
                        <div className="mt-2 text-center">
                          {editingSection === 6 ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel Edit
                            </Button>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditingSection(6)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>(H) LW Dept & Arbitrator</strong>
                    <p>Hearing (Conventional Hearing Day) *</p>
                    <Form.Group className="mb-2">
                      <Form.Label>Schedule Hearing</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="date"
                          value={conventionalHearingSchedule.date}
                          onChange={(e) =>
                            setConventionalHearingSchedule((prev) => ({
                              ...prev,
                              date: e.target.value,
                            }))
                          }
                          disabled={!isSectionEnabled(7)}
                        />
                        <Form.Control
                          type="time"
                          value={conventionalHearingSchedule.time}
                          onChange={(e) =>
                            setConventionalHearingSchedule((prev) => ({
                              ...prev,
                              time: e.target.value,
                            }))
                          }
                          disabled={!isSectionEnabled(7)}
                        />
                      </div>
                    </Form.Group>
                    <ul style={{ marginBottom: 0 }}>
                      <li>
                        Opening Statement of Claim
                        <UploadControl sectionIndex={7} docKey="Opening" />
                      </li>
                      <li>
                        Presentation of Evidences
                        <UploadControl sectionIndex={7} docKey="Evidences" />
                      </li>
                      <li>
                        Statement of Defence
                        <UploadControl sectionIndex={7} docKey="Defence" />
                      </li>
                      <li>
                        Final Damage Request by Parties
                        <UploadControl sectionIndex={7} docKey="FinalDamage" />
                      </li>
                      <li>
                        Arbitrator Questioning prior to Closing Hearing
                        <UploadControl sectionIndex={7} docKey="Questioning" />
                      </li>
                      <li>
                        Evaluation Survey form (to Parties) (E-H4)
                        <UploadControl sectionIndex={7} docKey="E-H4" />
                      </li>
                    </ul>
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">From</small>
                      <Form.Control
                        type="date"
                        value={row8.fromDate}
                        onChange={(e) =>
                          createHandler(setRow8)("fromDate", e.target.value)
                        }
                        className="mb-1"
                        disabled={true}
                      />
                      <small className="text-muted">To</small>
                      <Form.Control
                        type="date"
                        value={row8.toDate}
                        onChange={(e) =>
                          createHandler(setRow8)("toDate", e.target.value)
                        }
                        disabled={!isSectionEnabled(7)}
                      />
                    </div>
                  </td>
                  <td>10 Days</td>
                  <td>
                    <div>
                      <small className="text-muted">Exhibit No</small>
                      <Form.Control
                        type="text"
                        value={row8.exhibitNo}
                        onChange={(e) =>
                          createHandler(setRow8)("exhibitNo", e.target.value)
                        }
                        placeholder="Enter Exhibit No"
                        className="mb-1"
                        disabled={!isSectionEnabled(7)}
                      />
                      <small className="text-muted">No. of Pg.</small>
                      <Form.Control
                        type="number"
                        value={row8.pages}
                        onChange={(e) =>
                          createHandler(setRow8)("pages", e.target.value)
                        }
                        placeholder="No. of Pages"
                        className="mb-1"
                        disabled={!isSectionEnabled(7)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2 w-100">
                      <Button
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => handleSaveSection(7, row8)}
                        disabled={
                          !isSectionEnabled(7) || savingSection !== null
                        }
                      >
                        {savingSection === 7 ? "Saving..." : "Save"}
                      </Button>
                      {!isSectionEnabled(7) && sectionsMeta[7]?.isCompleted && (
                        <div className="mt-2 text-center">
                          {editingSection === 7 ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel Edit
                            </Button>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditingSection(7)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                      {sectionsMeta[7]?.meetings?.length > 0 && (
                        <MeetingJoinButton
                          meetings={sectionsMeta[7].meetings}
                        />
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>(I) LW Dept & Arbitrator</strong>
                    <p>Awards</p>
                    <ul style={{ marginBottom: 0 }}>
                      <li>
                        Checklist to Arbitrators (E-I1)
                        <UploadControl sectionIndex={8} docKey="E-I1" />
                      </li>
                      <li>
                        Feedback Form - Claimant (E-I2-Claimant)
                        <UploadControl
                          sectionIndex={8}
                          docKey="E-I2-Claimant"
                        />
                      </li>
                      <li>
                        Feedback Form - Respondent (E-I2-Respondent)
                        <UploadControl
                          sectionIndex={8}
                          docKey="E-I2-Respondent"
                        />
                      </li>
                      {sectionsMeta[2]?.arbitrators?.length > 1 ? (
                        sectionsMeta[2].arbitrators.map((arb, i) => (
                          <li key={`E-I3-Arb${i + 1}`}>
                            Copy of Award (in writing) - Arbitrator {i + 1} (
                            {arb.firstName} {arb.surName})
                            <UploadControl
                              sectionIndex={8}
                              docKey={`E-I3-Arb${i + 1}`}
                            />
                          </li>
                        ))
                      ) : (
                        <li>
                          Copy of Award (in writing) (E-I3)
                          <UploadControl sectionIndex={8} docKey="E-I3" />
                        </li>
                      )}
                    </ul>
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">From</small>
                      <Form.Control
                        type="date"
                        value={row9.fromDate}
                        onChange={(e) =>
                          createHandler(setRow9)("fromDate", e.target.value)
                        }
                        className="mb-1"
                        disabled={true}
                      />
                      <small className="text-muted">To</small>
                      <Form.Control
                        type="date"
                        value={row9.toDate}
                        onChange={(e) =>
                          createHandler(setRow9)("toDate", e.target.value)
                        }
                        disabled={!isSectionEnabled(8)}
                      />
                    </div>
                  </td>
                  <td>20 Days</td>
                  <td>
                    <div>
                      <small className="text-muted">Exhibit No</small>
                      <Form.Control
                        type="text"
                        value={row9.exhibitNo}
                        onChange={(e) =>
                          createHandler(setRow9)("exhibitNo", e.target.value)
                        }
                        placeholder="Enter Exhibit No"
                        className="mb-1"
                        disabled={!isSectionEnabled(8)}
                      />
                      <small className="text-muted">No. of Pg.</small>
                      <Form.Control
                        type="number"
                        value={row9.pages}
                        onChange={(e) =>
                          createHandler(setRow9)("pages", e.target.value)
                        }
                        placeholder="No. of Pages"
                        className="mb-1"
                        disabled={!isSectionEnabled(8)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2 w-100">
                      <Button
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => handleSaveSection(8, row9)}
                        disabled={
                          !isSectionEnabled(8) || savingSection !== null
                        }
                      >
                        {savingSection === 8 ? "Saving..." : "Save"}
                      </Button>
                      {!isSectionEnabled(8) && sectionsMeta[8]?.isCompleted && (
                        <div className="mt-2 text-center">
                          {editingSection === 8 ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel Edit
                            </Button>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditingSection(8)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>(J) LW Dept & Admin</strong>
                    <p>Post Award Services (By Center)</p>
                    <ul style={{ marginBottom: 0 }}>
                      <li>
                        Copies of Award (To Parties)
                        <UploadControl
                          sectionIndex={9}
                          docKey="CopiesOfAward"
                        />
                      </li>
                      <li>
                        Notices reminding parties of their obligations to comply
                        with the award
                        <UploadControl sectionIndex={9} docKey="Notices" />
                      </li>
                    </ul>
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">From</small>
                      <Form.Control
                        type="date"
                        value={row10.fromDate}
                        onChange={(e) =>
                          createHandler(setRow10)("fromDate", e.target.value)
                        }
                        className="mb-1"
                        disabled={true}
                      />
                      <small className="text-muted">To</small>
                      <Form.Control
                        type="date"
                        value={row10.toDate}
                        onChange={(e) =>
                          createHandler(setRow10)("toDate", e.target.value)
                        }
                        disabled={!isSectionEnabled(9)}
                      />
                    </div>
                  </td>
                  <td>10 Days</td>
                  <td>
                    <div>
                      <small className="text-muted">Exhibit No</small>
                      <Form.Control
                        type="text"
                        value={row10.exhibitNo}
                        onChange={(e) =>
                          createHandler(setRow10)("exhibitNo", e.target.value)
                        }
                        placeholder="Enter Exhibit No"
                        className="mb-1"
                        disabled={!isSectionEnabled(9)}
                      />
                      <small className="text-muted">No. of Pg.</small>
                      <Form.Control
                        type="number"
                        value={row10.pages}
                        onChange={(e) =>
                          createHandler(setRow10)("pages", e.target.value)
                        }
                        placeholder="No. of Pages"
                        className="mb-1"
                        disabled={!isSectionEnabled(9)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2 w-100">
                      <Button
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => handleSaveSection(9, row10)}
                        disabled={
                          !isSectionEnabled(9) || savingSection !== null
                        }
                      >
                        {savingSection === 9 ? "Saving..." : "Save"}
                      </Button>
                      {!isSectionEnabled(9) && sectionsMeta[9]?.isCompleted && (
                        <div className="mt-2 text-center">
                          {editingSection === 9 ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel Edit
                            </Button>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditingSection(9)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>(K) LW Dept</strong>
                    <ul style={{ marginBottom: 0 }}>
                      <li>
                        Correction and Interpretation of an Award, if Any (By
                        Parties)
                        <UploadControl
                          sectionIndex={10}
                          docKey="CorrectionInterpretation"
                        />
                      </li>
                    </ul>
                  </td>
                  <td>
                    <div>
                      <small className="text-muted">From</small>
                      <Form.Control
                        type="date"
                        value={row11.fromDate}
                        onChange={(e) =>
                          createHandler(setRow11)("fromDate", e.target.value)
                        }
                        className="mb-1"
                        disabled={true}
                      />
                      <small className="text-muted">To</small>
                      <Form.Control
                        type="date"
                        value={row11.toDate}
                        onChange={(e) =>
                          createHandler(setRow11)("toDate", e.target.value)
                        }
                        disabled={!isSectionEnabled(10)}
                      />
                    </div>
                  </td>
                  <td>15 Days</td>
                  <td>
                    <div>
                      <small className="text-muted">Exhibit No</small>
                      <Form.Control
                        type="text"
                        value={row11.exhibitNo}
                        onChange={(e) =>
                          createHandler(setRow11)("exhibitNo", e.target.value)
                        }
                        placeholder="Enter Exhibit No"
                        className="mb-1"
                        disabled={!isSectionEnabled(10)}
                      />
                      <small className="text-muted">No. of Pg.</small>
                      <Form.Control
                        type="number"
                        value={row11.pages}
                        onChange={(e) =>
                          createHandler(setRow11)("pages", e.target.value)
                        }
                        placeholder="No. of Pages"
                        className="mb-1"
                        disabled={!isSectionEnabled(10)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2 w-100">
                      <Button
                        variant="success"
                        size="sm"
                        className="w-100"
                        onClick={() => handleSaveSection(10, row11)}
                        disabled={
                          !isSectionEnabled(10) || savingSection !== null
                        }
                      >
                        {savingSection === 10 ? "Saving..." : "Save"}
                      </Button>
                      {!isSectionEnabled(10) &&
                        sectionsMeta[10]?.isCompleted && (
                          <div className="mt-2 text-center">
                            {editingSection === 10 ? (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => setEditingSection(null)}
                              >
                                Cancel Edit
                              </Button>
                            ) : (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setEditingSection(10)}
                              >
                                Edit
                              </Button>
                            )}
                          </div>
                        )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default BindingTimeTableModal;

const MeetingJoinButton = ({ meetings }) => {
  if (!Array.isArray(meetings) || meetings.length === 0) return null;

  const m = meetings[0];
  const joinUrl = `/room/${m.meetingRoomId}?username=${encodeURIComponent(
    "AECCI ADMINISTRATOR"
  )}`;

  // Extract date from meetingDate and use meetingTime directly
  const meetingDate = new Date(m.meetingDate);
  const dateStr = meetingDate.toLocaleDateString("en-US", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  // Convert 24-hour meetingTime to 12-hour format with AM/PM
  const formatTime = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div className="mt-2">
      <p className="mb-1 fw-bold">Meeting Scheduled:</p>
      <p className="mb-1 small">
        {dateStr}, {formatTime(m.meetingTime)}
      </p>
      <a
        href={joinUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-primary btn-sm w-100"
      >
        Join Meeting
      </a>
    </div>
  );
};
